import React, { useState, useEffect } from 'react';
import {
  Container,
  Title,
  Paper,
  Stack,
  ColorInput,
  TextInput,
  Button,
  Group,
  Loader,
  Alert,
  Grid,
  Box,
  Text,
} from '@mantine/core';
import { useForm } from '@mantine/form';
import { notifications } from '@mantine/notifications';
import { useAppDispatch, useAppSelector } from '../../../store/hooks';
import { fetchTheme, updateTheme } from '../../../store/slices/themeSlice';
import { ThemeSettings } from '../../../types/theme';

export const ManageThemePage: React.FC = () => {
  const dispatch = useAppDispatch();
  const currentTheme = useAppSelector((state) => state.theme);
  const [loading, setLoading] = useState(false);
  const [initialLoading, setInitialLoading] = useState(true);

  const form = useForm<ThemeSettings>({
    initialValues: currentTheme,
    validate: {
      primaryColor: (value) =>
        /^#([A-Fa-f0-9]{6})$/.test(value) ? null : 'Invalid hex color',
      secondaryColor: (value) =>
        /^#([A-Fa-f0-9]{6})$/.test(value) ? null : 'Invalid hex color',
      successColor: (value) =>
        /^#([A-Fa-f0-9]{6})$/.test(value) ? null : 'Invalid hex color',
      warningColor: (value) =>
        /^#([A-Fa-f0-9]{6})$/.test(value) ? null : 'Invalid hex color',
      errorColor: (value) =>
        /^#([A-Fa-f0-9]{6})$/.test(value) ? null : 'Invalid hex color',
      infoColor: (value) =>
        /^#([A-Fa-f0-9]{6})$/.test(value) ? null : 'Invalid hex color',
    },
  });

  useEffect(() => {
    dispatch(fetchTheme())
      .unwrap()
      .then((theme) => {
        form.setValues(theme);
      })
      .finally(() => setInitialLoading(false));
  }, [dispatch]);

  const handleSubmit = async (values: ThemeSettings) => {
    setLoading(true);
    try {
      await dispatch(updateTheme(values)).unwrap();
      notifications.show({
        title: 'Success',
        message: 'Theme updated successfully',
        color: 'green',
      });
    } catch {
      notifications.show({
        title: 'Error',
        message: 'Failed to update theme',
        color: 'red',
      });
    } finally {
      setLoading(false);
    }
  };

  if (initialLoading) {
    return (
      <Container className="flex justify-center items-center h-64">
        <Loader size="lg" />
      </Container>
    );
  }

  const ColorField = ({
    label,
    field,
    fallback,
  }: {
    label: string;
    field: keyof ThemeSettings;
    fallback: string;
  }) => (
    <Box>
      <Text size="sm" mb={6} fw={500}>
        {label}
      </Text>

      <ColorInput
        format="hex"
        w="100%"
        size="md"
        radius="md"
        withPicker
        swatches={[
          '#3b82f6',
          '#0ea5e9',
          '#22c55e',
          '#f59e0b',
          '#ef4444',
          '#8b5cf6',
        ]}
        value={form.values[field]}
        onChangeEnd={(value) =>
          form.setFieldValue(field, value || fallback)
        }
      />
    </Box>
  );

  return (
    <Container size="lg">
      <Title order={2} mb="lg">
        🎨 Manage Theme
      </Title>

      <Paper withBorder p="xl" radius="lg" shadow="sm">
        <form onSubmit={form.onSubmit(handleSubmit)}>
          <Stack>

            {/* Alert with dynamic color */}
            <Alert
              style={{
                background: 'var(--info-color)',
                color: '#fff',
              }}
            >
              Changes will be applied immediately across the application.
            </Alert>

            {/* Color Section */}
            <Grid>
              <Grid.Col span={6}>
                <ColorField label="Primary Color" field="primaryColor" fallback="#3b82f6" />
              </Grid.Col>
              <Grid.Col span={6}>
                <ColorField label="Secondary Color" field="secondaryColor" fallback="#0ea5e9" />
              </Grid.Col>
              <Grid.Col span={6}>
                <ColorField label="Success Color" field="successColor" fallback="#22c55e" />
              </Grid.Col>
              <Grid.Col span={6}>
                <ColorField label="Warning Color" field="warningColor" fallback="#f59e0b" />
              </Grid.Col>
              <Grid.Col span={6}>
                <ColorField label="Error Color" field="errorColor" fallback="#ef4444" />
              </Grid.Col>
              <Grid.Col span={6}>
                <ColorField label="Info Color" field="infoColor" fallback="#3b82f6" />
              </Grid.Col>
            </Grid>

            {/* Font */}
            <TextInput
              label="Font Family"
              placeholder="Poppins, sans-serif"
              radius="md"
              size="md"
              {...form.getInputProps('fontFamily')}
            />

            {/* 🔥 Improved Preview */}
            <Box
              mt="md"
              p="md"
              style={{
                borderRadius: '8px',
                background: 'var(--primary-color)',
                color: '#fff',
                fontFamily: 'var(--font-family)',
              }}
            >
              <Text fw={500}>Live Preview</Text>
              <Text size="sm">
                Primary color applied here
              </Text>

              <Group mt="sm">
                <Button style={{ background: 'var(--secondary-color)' }}>
                  Secondary
                </Button>
                <Button style={{ background: 'var(--success-color)' }}>
                  Success
                </Button>
                <Button style={{ background: 'var(--warning-color)' }}>
                  Warning
                </Button>
                <Button style={{ background: 'var(--error-color)' }}>
                  Error
                </Button>
              </Group>
            </Box>

            {/* Submit */}
            <Group justify="flex-end" mt="lg">
              <Button
                type="submit"
                size="md"
                radius="md"
                loading={loading}
                style={{
                  background: 'var(--primary-color)',
                }}
              >
                Save Theme
              </Button>
            </Group>

          </Stack>
        </form>
      </Paper>
    </Container>
  );
};